import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, errText } from "@/lib/api";
import { toast } from "sonner";
import { Gift, ArrowRight, ArrowUpRight, Check, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import { trackFormStart, trackLead } from "@/lib/analytics";

const TIERS = [
  {
    slug: "single-pt",
    label: "Single 1-to-1 Personal Training Session",
    price: 60,
    tag: "For a taste",
    detail: "One 60-minute private coached session. The perfect stocking filler — or a nudge back into training after a while away.",
  },
  {
    slug: "weekly-pt-month",
    label: "Month of Weekly 1-to-1 Personal Training",
    price: 220,
    tag: "Most gifted",
    detail: "Four private 1-to-1 sessions across a month, plus a written weekly plan. A meaningful gift that actually changes how someone feels.",
    highlighted: true,
  },
  {
    slug: "kore-access-month",
    label: "One Month · Kore Access Membership",
    price: 49,
    tag: "Full access",
    detail: "A month of open gym, community and classes. Great for someone curious about training in a coached, small-club environment.",
  },
  {
    slug: "kore-coached-month",
    label: "One Month · Kore Coaching Membership",
    price: 149,
    tag: "The full experience",
    detail: "Small-group coaching twice a week, unlimited classes, nutrition support and monthly check-ins. The complete Kore experience for a month.",
  },
];

const HOW = [
  { n: "01", t: "Choose a gift.", d: "Pick the voucher that fits the person you have in mind." },
  { n: "02", t: "Add your details.", d: "Recipient name, email, a short message and when you'd like it delivered." },
  { n: "03", t: "We invoice you.", d: "A short payment link lands in your inbox within one working day." },
  { n: "04", t: "They receive it.", d: "Once paid, a beautifully-designed gift email arrives with a unique voucher code." },
];

export default function GiftVouchers() {
  const [tier, setTier] = useState(TIERS[1]);
  const [form, setForm] = useState({
    purchaser_name: "", purchaser_email: "",
    recipient_name: "", recipient_email: "",
    message: "", deliver_on: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/gift-vouchers/reserve", {
        tier_slug: tier.slug,
        tier_label: tier.label,
        amount_gbp: tier.price,
        ...form,
        deliver_on: form.deliver_on || null,
        message: form.message || null,
      });
      setDone(true);
      trackLead({ form_name: "gift_voucher", enquiry_type: "gift_voucher" });
      toast.success("Order received — we'll be in touch with a payment link.");
    } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="gift-vouchers-page">
      <Seo
        title="Gift Vouchers — Kore Wellbeing"
        description="Give the gift of coached training, wellbeing and community. Kore Wellbeing gift vouchers for 1-to-1 PT, memberships and single sessions — thoughtful, meaningful, and valid for 12 months."
        path="/gift-vouchers"
      />

      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden bg-kore-forest">
        <div className="absolute inset-0 bg-gradient-to-br from-kore-forest via-kore-forest to-kore-forest/90" />
        <div className="absolute inset-0 opacity-30">
          <img src="/gallery/community-social.webp" alt="" loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div className="relative container-kore pb-20 md:pb-28 max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-kore-light">
              <Sparkles size={14} /> Kore gift vouchers
            </div>
            <h1 className="mt-6 font-display font-light text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              A gift that quietly<br/>
              <span className="italic text-kore-light">changes someone's year.</span>
            </h1>
            <p className="mt-8 max-w-xl text-kore-warm/85 text-base sm:text-lg font-light leading-relaxed">
              Coached training, meaningful community and honest support — wrapped up as a Christmas or birthday gift.
              Choose a voucher, add a personal note, and we'll take care of the rest.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#choose" className="btn-gold">Choose a gift <ArrowRight size={16} /></a>
              <Link to="/contact" className="btn-ghost !text-kore-warm !border-kore-warm/30">Custom amount? Get in touch</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TIERS */}
      <section id="choose" className="container-kore py-24">
        <div className="max-w-2xl mb-14">
          <span className="eyebrow">Choose a gift</span>
          <h2 className="display-2 mt-4 text-kore-forest">Four thoughtful options.</h2>
          <p className="mt-6 text-kore-ink/70 leading-relaxed">All vouchers are valid for 12 months from the date of issue. Not sure which to choose? The monthly Kore Coaching voucher is the closest thing we have to giving someone a proper fresh start.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TIERS.map((t) => {
            const selected = tier.slug === t.slug;
            return (
              <button
                key={t.slug}
                type="button"
                onClick={() => { setTier(t); document.getElementById("gift-form")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                data-testid={`gift-tier-${t.slug}`}
                className={`relative rounded-3xl p-7 text-left transition-all duration-300 flex flex-col ${
                  selected
                    ? "bg-kore-forest text-kore-warm ring-2 ring-kore-light shadow-lift"
                    : t.highlighted
                      ? "bg-white border-2 border-kore-gold/50 hover:shadow-lift"
                      : "bg-white border border-kore-gold/20 hover:shadow-lift"
                }`}
              >
                {t.highlighted && !selected && (
                  <span className="absolute -top-3 left-6 bg-kore-light text-kore-forest text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full">Most gifted</span>
                )}
                {selected && (
                  <span className="absolute -top-3 right-6 bg-kore-light text-kore-forest text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full flex items-center gap-1"><Check size={11} /> Selected</span>
                )}
                <div className={`text-[10px] uppercase tracking-[0.22em] ${selected ? "text-kore-light" : "text-kore-gold"}`}>{t.tag}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className={`font-display text-4xl font-light ${selected ? "text-kore-warm" : "text-kore-forest"}`}>£{t.price}</span>
                </div>
                <h3 className={`mt-4 font-display text-xl leading-tight ${selected ? "text-kore-warm" : "text-kore-forest"}`}>{t.label}</h3>
                <p className={`mt-3 text-sm leading-relaxed flex-1 ${selected ? "text-kore-warm/80" : "text-kore-ink/70"}`}>{t.detail}</p>
                <div className={`mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] ${selected ? "text-kore-light" : "text-kore-forest"}`}>
                  {selected ? "Selected" : "Choose"} <ArrowRight size={12} />
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-kore-light/25 py-24">
        <div className="container-kore grid md:grid-cols-12 gap-14">
          <div className="md:col-span-4">
            <span className="eyebrow">How it works</span>
            <h2 className="display-2 mt-4 text-kore-forest">Four small steps.</h2>
            <p className="mt-6 text-kore-ink/70 leading-relaxed font-light max-w-sm">No self-checkout, no third-party links you don't recognise — just a warm, personal handoff from our team to yours.</p>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-8">
            {HOW.map((s) => (
              <div key={s.n} className="flex gap-6 border-b border-kore-gold/20 pb-6">
                <div className="text-kore-gold text-sm font-medium w-10 shrink-0">{s.n}</div>
                <div>
                  <h3 className="font-display text-2xl text-kore-forest">{s.t}</h3>
                  <p className="mt-2 text-kore-ink/75 leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="gift-form" className="container-kore py-24 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <span className="eyebrow">Complete your order</span>
          <h2 className="display-2 mt-4 text-kore-forest">Just a few<br/>quick details.</h2>
          <div className="mt-8 rounded-3xl bg-kore-warm border border-kore-gold/25 p-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-kore-gold flex items-center gap-2"><Gift size={13} /> Your gift</div>
            <div className="mt-3 font-display text-2xl text-kore-forest leading-tight">{tier.label}</div>
            <div className="mt-2 text-sm text-kore-ink/60">£{tier.price.toFixed(2)}</div>
            <button type="button" onClick={() => document.getElementById("choose")?.scrollIntoView({ behavior: "smooth" })} className="mt-4 text-xs uppercase tracking-[0.2em] text-kore-forest border-b border-kore-gold pb-1">Change gift</button>
          </div>
          <p className="mt-8 text-sm text-kore-ink/60 leading-relaxed">
            Vouchers are valid for 12 months from date of issue and are non-refundable. If you'd like a physical printed card as well, add a note in the message field — we're happy to post one.
          </p>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          <div className="bg-white rounded-3xl p-10 shadow-soft border border-kore-gold/10">
            {!done ? (
              <form onSubmit={submit} onFocus={() => trackFormStart({ form_name: "gift_voucher" })} data-testid="gift-form" className="space-y-6">
                <div>
                  <div className="eyebrow mb-3 text-kore-forest">Your details</div>
                  <div className="grid gap-4">
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.22em] text-kore-ink/60">Your name</label>
                      <input required data-testid="gift-purchaser-name" value={form.purchaser_name} onChange={(e) => setForm({ ...form, purchaser_name: e.target.value })} className="mt-2 w-full border-b border-kore-ink/20 py-2 focus:outline-none focus:border-kore-gold bg-transparent" />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.22em] text-kore-ink/60">Your email</label>
                      <input required type="email" data-testid="gift-purchaser-email" value={form.purchaser_email} onChange={(e) => setForm({ ...form, purchaser_email: e.target.value })} className="mt-2 w-full border-b border-kore-ink/20 py-2 focus:outline-none focus:border-kore-gold bg-transparent" />
                    </div>
                  </div>
                </div>

                <div>
                  <div className="eyebrow mb-3 text-kore-forest">Their details</div>
                  <div className="grid gap-4">
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.22em] text-kore-ink/60">Their name</label>
                      <input required data-testid="gift-recipient-name" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="mt-2 w-full border-b border-kore-ink/20 py-2 focus:outline-none focus:border-kore-gold bg-transparent" />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.22em] text-kore-ink/60">Their email</label>
                      <input required type="email" data-testid="gift-recipient-email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="mt-2 w-full border-b border-kore-ink/20 py-2 focus:outline-none focus:border-kore-gold bg-transparent" />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.22em] text-kore-ink/60">Deliver on (optional)</label>
                      <input type="date" data-testid="gift-deliver-on" value={form.deliver_on} onChange={(e) => setForm({ ...form, deliver_on: e.target.value })} className="mt-2 w-full border-b border-kore-ink/20 py-2 focus:outline-none focus:border-kore-gold bg-transparent" />
                      <p className="text-[10px] text-kore-ink/50 mt-1">Leave blank to send as soon as payment is confirmed.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-kore-ink/60">A short message (optional)</label>
                  <textarea rows={3} data-testid="gift-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Something warm — from you to them." className="mt-2 w-full border border-kore-ink/15 rounded-lg p-3 focus:outline-none focus:border-kore-gold bg-transparent resize-none text-sm" />
                </div>

                <button disabled={busy} data-testid="gift-submit" className="btn-primary w-full mt-2">
                  {busy ? "Sending…" : (<>Reserve this gift · £{tier.price.toFixed(2)} <ArrowRight size={14} /></>)}
                </button>
                <p className="text-[11px] text-kore-ink/50 leading-relaxed">
                  We'll email you a short payment link within one working day. No card details entered here.
                </p>
              </form>
            ) : (
              <div data-testid="gift-success" className="text-center py-6">
                <div className="w-14 h-14 mx-auto rounded-full bg-kore-light flex items-center justify-center text-kore-forest">
                  <Gift size={22} />
                </div>
                <h3 className="font-display text-3xl mt-6 text-kore-forest">Order received.</h3>
                <p className="mt-3 text-kore-ink/70 max-w-sm mx-auto">Thanks for choosing Kore. We'll send you a payment link within one working day — once paid, the voucher lands straight in their inbox.</p>
                <div className="mt-8 flex gap-3 justify-center flex-wrap">
                  <Link to="/" className="btn-ghost">Back to home</Link>
                  <Link to="/personal-training" className="btn-ghost">See 1-to-1 PT <ArrowUpRight size={12} /></Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
